// Lightning-bolt UI: bind a single block setting to a dynamic source.
// The picker is intentionally lightweight — full power lives in the
// runtime resolver. This is the editor surface to author the binding.

import { useRef, useState } from 'react'
import { Zap, X } from 'lucide-react'

import { FloatingPanel } from './FloatingPanel'
import type { BindingExpr, PageDataSource, PageInput } from './types'
import { useProjectVariables, type ProjectVarOption } from './useProjectVariables'

interface Props {
  value?: BindingExpr                // current binding (undefined = none)
  onChange: (expr: BindingExpr | undefined) => void
  inputs: PageInput[]
  metafieldNames?: string[]          // optional list of known metafields
  resourceCandidates?: Array<{ obj_type: string; obj_name: string; label: string }>
  dataSources?: PageDataSource[]     // page data sources, for data_field dropdown
  project?: string                   // enables Project-variable options
  buttonLabel?: string
}

export function BindingPicker({
  value, onChange, inputs, metafieldNames = [], resourceCandidates = [], dataSources = [], project, buttonLabel,
}: Props) {
  const projectVars = useProjectVariables(project)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const isBound = !!value

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <button
        ref={anchorRef}
        type="button"
        title={isBound ? `Bound: ${describe(value!)}` : 'Bind to dynamic source'}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 22, height: 22, padding: 0,
          border: '1px solid var(--color-border)',
          background: isBound ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
          color: isBound ? '#fff' : 'var(--color-text-secondary)',
          borderRadius: 4, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Zap size={12} />
      </button>
      {buttonLabel && <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{buttonLabel}</span>}
      {open && (
        <FloatingPanel anchorRef={anchorRef} align="right" width={280} onClose={() => setOpen(false)}>
          <div
            style={{
              padding: 12,
              background: 'var(--color-bg)', border: '1px solid var(--color-border)',
              borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
              display: 'grid', gap: 8,
            }}
          >
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: 13 }}>Bind dynamic source</strong>
              <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
                <X size={14} />
              </button>
            </header>
            <BindingEditor
              value={value}
              onChange={v => onChange(v)}
              inputs={inputs}
              projectVars={projectVars}
              metafieldNames={metafieldNames}
              resourceCandidates={resourceCandidates}
              dataSources={dataSources}
            />
            {isBound && (
              <button
                type="button"
                onClick={() => { onChange(undefined); setOpen(false) }}
                style={{
                  padding: '6px 8px', fontSize: 13, fontWeight: 500,
                  border: '1px solid var(--color-border)', borderRadius: 4,
                  background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
                  cursor: 'pointer',
                }}
              >
                Remove binding
              </button>
            )}
          </div>
        </FloatingPanel>
      )}
    </div>
  )
}

function describe(b: BindingExpr): string {
  switch (b.source) {
    case 'static': return `static: ${String(b.value).slice(0, 32)}`
    case 'page_input': return `input: ${b.name ?? '(none)'}`
    case 'metafield': return `metafield: ${b.name ?? '(none)'}`
    case 'resource_field': return `${b.resource?.obj_name ?? '?'}.${b.path ?? '?'}`
    case 'data_field': return `data: ${b.dataSource ?? '?'}.${b.path ?? '?'}`
    case 'item_field': return `item${b.scope ? `(${b.scope})` : ''}.${b.path ?? '?'}`
    case 'expression': return b.expression?.slice(0, 32) ?? ''
    default: return ''
  }
}

interface EditorProps {
  value: BindingExpr | undefined
  onChange: (v: BindingExpr) => void
  inputs: PageInput[]
  projectVars: ProjectVarOption[]
  metafieldNames: string[]
  resourceCandidates: Array<{ obj_type: string; obj_name: string; label: string }>
  dataSources: PageDataSource[]
}
function BindingEditor({ value, onChange, inputs, projectVars, metafieldNames, resourceCandidates, dataSources }: EditorProps) {
  const v: BindingExpr = value ?? { source: 'page_input' }
  const set = (patch: Partial<BindingExpr>): void => onChange({ ...v, ...patch })
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div>
        <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Source</label>
        <select
          value={v.source}
          onChange={e => set({ source: e.target.value as BindingExpr['source'] })}
          style={fieldStyle()}
        >
          <option value="page_input">Page input</option>
          <option value="metafield">Metafield</option>
          <option value="resource_field">Resource field</option>
          <option value="data_field">Data source field</option>
          <option value="item_field">Row field (repeater)</option>
          <option value="expression">Expression</option>
          <option value="static">Static value</option>
        </select>
      </div>
      {v.source === 'data_field' && (
        <>
          {dataSources.length > 0 ? (
            <select
              value={v.dataSource ?? ''}
              onChange={e => set({ dataSource: e.target.value })}
              style={fieldStyle()}
            >
              <option value="">(choose data source)</option>
              {dataSources.map(s => (
                <option key={s.id} value={s.id}>
                  {s.id} — {s.resource?.obj_name} ({s.mode})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={v.dataSource ?? ''}
              onChange={e => set({ dataSource: e.target.value })}
              placeholder="data source id (record mode)"
              style={fieldStyle()}
            />
          )}
          <input
            type="text"
            value={v.path ?? ''}
            onChange={e => set({ path: e.target.value })}
            placeholder="column (e.g. EVENT_NAME)"
            style={fieldStyle()}
          />
        </>
      )}
      {v.source === 'item_field' && (
        <>
          <input
            type="text"
            value={v.path ?? ''}
            onChange={e => set({ path: e.target.value })}
            placeholder="row column (e.g. name)"
            style={fieldStyle()}
          />
          <input
            type="text"
            value={v.scope ?? ''}
            onChange={e => set({ scope: e.target.value || undefined })}
            placeholder="scope alias (optional, for nesting)"
            style={fieldStyle()}
          />
        </>
      )}
      {v.source === 'page_input' && (
        <>
          <select value={v.name ?? ''} onChange={e => set({ name: e.target.value })} style={fieldStyle()}>
            <option value="">(choose input)</option>
            {inputs.length > 0 && (
              <optgroup label="Page inputs">
                {inputs.map(i => <option key={i.name} value={i.name}>{i.label} — {i.name}</option>)}
              </optgroup>
            )}
            {projectVars.length > 0 && (
              <optgroup label="Project variables">
                {projectVars.map(pv => <option key={pv.value} value={pv.value}>{pv.label}</option>)}
              </optgroup>
            )}
            {/* Keep the current binding visible even if its input was removed
                or isn't in this page's input list (e.g. site chrome pages). */}
            {v.name && !inputs.some(i => i.name === v.name) && !projectVars.some(p => p.value === v.name) && (
              <option value={v.name}>{v.name} (not on this page)</option>
            )}
          </select>
          {inputs.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.4 }}>
              No page inputs defined. Add one in the left panel (Page Inputs), or bind to a metafield / data source instead.
            </p>
          )}
        </>
      )}
      {v.source === 'metafield' && (
        <input
          type="text"
          value={v.name ?? ''}
          onChange={e => set({ name: e.target.value })}
          placeholder="metafield name"
          list="binding-metafields"
          style={fieldStyle()}
        />
      )}
      {v.source === 'metafield' && metafieldNames.length > 0 && (
        <datalist id="binding-metafields">
          {metafieldNames.map(n => <option key={n} value={n} />)}
        </datalist>
      )}
      {v.source === 'resource_field' && (
        <>
          <select
            value={v.resource ? `${v.resource.obj_type}:${v.resource.obj_name}` : ''}
            onChange={e => {
              const [obj_type, obj_name] = e.target.value.split(':')
              set({ resource: { obj_type, obj_name } })
            }}
            style={fieldStyle()}
          >
            <option value="">(choose resource)</option>
            {resourceCandidates.map(r => (
              <option key={`${r.obj_type}:${r.obj_name}`} value={`${r.obj_type}:${r.obj_name}`}>{r.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={v.path ?? ''}
            onChange={e => set({ path: e.target.value })}
            placeholder="dotted.path.to.field"
            style={fieldStyle()}
          />
        </>
      )}
      {v.source === 'expression' && (
        <input
          type="text"
          value={v.expression ?? ''}
          onChange={e => set({ expression: e.target.value })}
          placeholder="{{count}} > 0"
          style={fieldStyle()}
        />
      )}
      {v.source === 'static' && (
        <input
          type="text"
          value={v.value == null ? '' : String(v.value)}
          onChange={e => set({ value: e.target.value })}
          placeholder="static value"
          style={fieldStyle()}
        />
      )}
      <input
        type="text"
        value={v.fallback == null ? '' : String(v.fallback)}
        onChange={e => set({ fallback: e.target.value })}
        placeholder="Fallback value (optional)"
        style={{ ...fieldStyle(), fontSize: 13 }}
      />
    </div>
  )
}

function fieldStyle(): React.CSSProperties {
  return {
    width: '100%', padding: '6px 8px', fontSize: 13,
    border: '1px solid var(--color-border)', borderRadius: 4,
    background: 'var(--color-bg)', color: 'var(--color-text)',
  }
}
